import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Clock } from "lucide-react";
import { cn } from "../../../lib/utils";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { AlertBanner, ContentCard, PageHeader, ui } from "../../../components/ui";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Calendar } from "../../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { getAuthenticatedEmployeeId } from "../../../lib/employee";
import { getReport, regularize } from "../../../services/attendanceService";
import { useNavigate, useLocation } from "react-router-dom";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

function toIsoDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  return `${dateStr}T${timeStr}:00`;
}

function toIsoFromServer(serverStr) {
  if (!serverStr) return null;
  const [datePart, rest] = serverStr.split(/[T ]/);
  if (!datePart || !rest) return null;
  const [h, m, s] = rest.split(":");
  const seconds = (s || "00").split(".")[0];
  return `${datePart}T${h}:${m}:${seconds}`;
}

function formatTimeInput(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
}

function formatDisplayTime(serverStr) {
  const parsed = toIsoFromServer(serverStr);
  if (!parsed) return "--";
  return parsed.split("T")[1].slice(0, 5);
}

// Text input for typing "HH:MM" plus a small clock icon that opens
// an HH / MM dropdown picker. Both write to the same onChange value.
function TimeField({ value, onChange, disabled, placeholder }) {
  const [hh, mm] = value && /^\d{2}:\d{2}$/.test(value) ? value.split(":") : ["", ""];

  const setPart = (part, next) => {
    const nextHh = part === "hh" ? next : hh || "00";
    const nextMm = part === "mm" ? next : mm || "00";
    onChange(`${nextHh}:${nextMm}`);
  };

  return (
    <div className="mt-1 flex items-center gap-1">
      <input
        disabled={disabled}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(formatTimeInput(e.target.value))}
        className="h-10 rounded-xl w-full border border-(--color-text-border) px-3 text-sm disabled:bg-workspace/40" />

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="h-10 w-10 shrink-0 flex items-center justify-center rounded-xl border border-(--color-text-border) bg-white hover:bg-workspace/40 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={i18n.t("dateTime.pickTime")}>

            <Clock className="h-4 w-4 per-file-hint" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <div className="flex items-center gap-2">
            <Select value={hh} onValueChange={(v) => setPart("hh", v)}>
              <SelectTrigger className="w-[70px]">
                <SelectValue placeholder={i18n.t("attendance.hh")} />
              </SelectTrigger>
              <SelectContent>
                {HOURS.map((h) =>
                <SelectItem key={h} value={h}>{h}</SelectItem>
                )}
              </SelectContent>
            </Select>
            <span className="text-sm font-semibold">:</span>
            <Select value={mm} onValueChange={(v) => setPart("mm", v)}>
              <SelectTrigger className="w-[70px]">
                <SelectValue placeholder={i18n.t("attendance.mm")} />
              </SelectTrigger>
              <SelectContent>
                {MINUTES.map((m) =>
                <SelectItem key={m} value={m}>{m}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </PopoverContent>
      </Popover>
    </div>);

}

export function RegularizationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [attendanceId, setAttendanceId] = useState("");
  const [originalRecord, setOriginalRecord] = useState(null);
  const [requestedCheckIn, setRequestedCheckIn] = useState("");
  const [requestedCheckOut, setRequestedCheckOut] = useState("");
  const [reason, setReason] = useState("");
  const [lookupError, setLookupError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const incomingDate = location.state?.date;
  const [selectedDate, setSelectedDate] = useState(
    incomingDate ? new Date(`${incomingDate}T00:00:00`) : undefined
  );
  const date = selectedDate ?
  `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}` :
  "";

  useEffect(() => {
    const empId = getAuthenticatedEmployeeId();
    if (!empId || !date) {
      setAttendanceId("");
      setOriginalRecord(null);
      setLookupError("");
      return;
    }
    getReport(date, date)
    .then((rows) => {
      const row = Array.isArray(rows) ? rows[0] : rows;
      if (row?.attendanceId) {
        setAttendanceId(row.attendanceId);
        setOriginalRecord(row);
        setLookupError(
          !row.checkOutTime ?
          "You must check out before you can request regularization for this day." :
          ""
        );
      } else {
        setAttendanceId("");
        setOriginalRecord(null);
        setLookupError("No attendance record found for this date.");
      }
    })
    .catch((err) => {
      setAttendanceId("");
      setOriginalRecord(null);
      setLookupError(`Could not fetch attendance record: ${err.message}`);
    });
  }, [date]);

  // Autopopulate requested check-in/check-out from the existing record.
  useEffect(() => {
    if (!originalRecord) {
      setRequestedCheckIn("");
      setRequestedCheckOut("");
      return;
    }
    const oldIn = toIsoFromServer(originalRecord.checkInTime);
    const oldOut = toIsoFromServer(originalRecord.checkOutTime);
    setRequestedCheckIn(oldIn ? oldIn.split("T")[1].slice(0, 5) : "");
    setRequestedCheckOut(oldOut ? oldOut.split("T")[1].slice(0, 5) : "");
  }, [originalRecord]);

  const hasCheckedOut = !!originalRecord?.checkOutTime;

  const canSubmit =
  date &&
  attendanceId &&
  hasCheckedOut &&
  reason.trim() &&
  /^\d{2}:\d{2}$/.test(requestedCheckIn) &&
  /^\d{2}:\d{2}$/.test(requestedCheckOut) &&
  !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await regularize({
        attendanceId: attendanceId || null,
        date,
        requestedCheckInTime: toIsoDateTime(date, requestedCheckIn),
        requestedCheckOutTime: toIsoDateTime(date, requestedCheckOut),
        reason: reason.trim()
      });
      navigate("/hrms-ess/attendance");
    } catch (err) {
      setSubmitError(err.message || "Failed to submit regularization request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout formLayout="compact">
          <div className="w-full">
            <PageHeader back={<PageBackLink to="/hrms-ess/attendance" />}
          title={i18n.t("attendance.attendanceRegularization")}
          />

            <ContentCard bodyClassName="form-section">
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-foreground">
                <Trans i18nKey="dateTime.date" /> <span className="text-red-500">*</span>
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "mt-1 w-full h-11 justify-start text-left font-normal rounded-xl border-(--color-text-border) bg-white hover:bg-workspace/40 px-4",
                      !selectedDate && "text-muted-foreground"
                    )}>

                    <CalendarIcon className="mr-2 h-4 w-4 per-file-hint" />
                    {selectedDate ? format(selectedDate, "PPP") : <span><Trans i18nKey="dateTime.selectDate2" /></span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")} />

                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="text-sm font-semibold text-foreground">
                <Trans i18nKey="attendance.attendanceId" /> <span className="text-red-500">*</span>
              </label>
              <input className="mt-1 h-10 rounded-xl w-full border border-(--color-text-border) px-3 text-sm" value={attendanceId || (date ? "No record found" : "Select a date first")} readOnly />
              {lookupError ? <p className="text-xs text-error mt-1">{lookupError}</p> : null}
            </div>
            {originalRecord &&
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm rounded-xl border border-(--color-text-border) bg-workspace/30 p-3 form-grid">
              <div>
                <span className="font-semibold text-foreground">Old Check-In: </span>
                <span className="per-file-hint">{formatDisplayTime(originalRecord.checkInTime)}</span>
              </div>
              <div>
                <span className="font-semibold text-foreground">Old Check-Out: </span>
                <span className="per-file-hint">{formatDisplayTime(originalRecord.checkOutTime)}</span>
              </div>
            </div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 form-grid">
              <div>
                <label className="text-sm font-semibold text-foreground">
                  <Trans i18nKey="formActions.requestedCheckIn" /> <span className="text-red-500">*</span>
                </label>
                <TimeField
                  disabled={!hasCheckedOut}
                  placeholder={i18n.t("attendance.hhMm")}
                  value={requestedCheckIn}
                  onChange={setRequestedCheckIn} />

              </div>
              <div>
                <label className="text-sm font-semibold text-foreground">
                  <Trans i18nKey="formActions.requestedCheckOut" /> <span className="text-red-500">*</span>
                </label>
                <TimeField
                  disabled={!hasCheckedOut}
                  placeholder={i18n.t("attendance.hhMm")}
                  value={requestedCheckOut}
                  onChange={setRequestedCheckOut} />

              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-foreground">
                <Trans i18nKey="common.reason" /> <span className="text-red-500">*</span>
              </label>
              <Textarea className="mt-1 rounded-xl" value={reason} onChange={(e) => setReason(e.target.value)} required />
            </div>
            {submitError ? <AlertBanner type="error">{submitError}</AlertBanner> : null}
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 pt-2 form-actions">
              <Button
                type="button"
                variant="outline"
                className="h-11 px-6 rounded-xl border-primary/30 text-primary font-semibold text-sm hover:bg-workspace/40 bg-white"
                onClick={() => navigate("/hrms-ess/attendance")}>
                <Trans i18nKey="formActions.cancel" />

              </Button>
              <Button type="submit" disabled={!canSubmit} className={`${ui.primaryBtn} min-w-[130px]`}>
                {submitting ? "Submitting..." : "Submit Request"}
              </Button>
            </div>
          </form>
            </ContentCard>
          </div>
    </AppLayout>);

}